import { Download, FileText, Image as ImageIcon, Paperclip } from 'lucide-react'
import { agentApi, type AgentAttachment } from '@/lib/api/agent'
import { Badge } from '@/components/ui/badge'

/** Bytes as an operator reads them, not as a machine stores them. */
function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Every file in one conversation.
 *
 * Attachments render inline with the message that carried them, which is right
 * while reading and useless when looking for a bill of lading uploaded twenty
 * messages ago. This is the index.
 */
export function ArtifactPanel({ attachments }: { attachments: AgentAttachment[] }) {
  if (attachments.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <Paperclip className="mb-2 size-4" />
        <p>No files yet.</p>
        <p className="mt-1 text-xs">
          Drop a screenshot or a bill of lading into the composer, or paste one.
        </p>
      </div>
    )
  }

  return (
    <ul className="divide-y divide-border">
      {attachments.map((attachment) => {
        const isImage = attachment.content_type.startsWith('image/')
        return (
          <li key={attachment.id} className="flex min-w-0 items-center gap-3 p-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded bg-muted">
              {isImage ? (
                <ImageIcon className="size-4 text-muted-foreground" />
              ) : (
                <FileText className="size-4 text-muted-foreground" />
              )}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{attachment.file_name}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {readableSize(attachment.file_size_bytes)}
              </span>
            </span>

            {attachment.document_id && (
              <Badge variant="secondary" className="shrink-0 text-xs">
                Filed
              </Badge>
            )}

            <a
              href={agentApi.attachmentUrl(attachment.id)}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Download ${attachment.file_name}`}
            >
              <Download className="size-4" />
            </a>
          </li>
        )
      })}
    </ul>
  )
}
